/**
 * Which of a client's asset rates are THEIR OWN figures rather than the published assumption, and
 * the same fact-find with those figures put back to the defaults.
 *
 * A client (or the person entering their profile) may set the growth or income rate on any asset —
 * Moneysmart's own planner allows it, and so do we. But a Statement of Advice is personal advice
 * given under a licence, so a rate the client chose must be reported as their decision, with the
 * consequence shown in dollars: the same plan run once more at our default rates. That comparison
 * is only honest if it changes NOTHING but the rates — same assets sold, same order, same deposit,
 * same borrowing — which is why this resets the rates on a fact-find the plan has already been
 * applied to, rather than asking the engine to choose a plan afresh.
 *
 * What counts as the client's own figure:
 *   - Bank, term deposits, shares: a rate that differs from the published asset-class default.
 *   - Investment property: the growth rate only. Its income is the recorded rent in dollars, not a
 *     yield (see assetAssumptionRows.ts), so there is no "income rate" to have changed.
 *   - Super and account-based pensions: a rate matching NO investment option. Picking "Growth" from
 *     the dropdown is choosing a published assumption, not writing one; only a hand-typed figure
 *     counts, and it is measured against the Balanced default the profile form starts from.
 * A field left blank is not a choice — the engine applies its default — so it is never reported.
 *
 * Read at use time against the ACTIVE tables, so a firm's own capital market assumptions are the
 * defaults where a firm has set them (investmentProfiles.ts).
 */
import { ASSET_CLASS_ASSUMPTIONS, INVESTMENT_PROFILES, profileForSuperRate } from './investmentProfiles.ts';
import type { FactFindAsset, FactFindAssetMap } from './factFindTypes.ts';

/**
 * The growth rate a new fact-find starts the family home on (% p.a.) — the one the profile form
 * shows before anyone touches it. The home has no asset-class row in the firm tables, so this is the
 * default a changed figure is measured against. (server/src/rest/calculation/grannyFlatFactFind.ts
 * carries the same 5 for a retained home.)
 */
export const DEFAULT_HOME_GROWTH_PCT = 5;

/** What this helper needs of a fact-find: its assets, and the home's own growth field. */
interface RateBearing {
    assets?: FactFindAssetMap;
    home?: { homeGrowthRate?: number } | null;
}

export interface AssetRateOverride {
    /** The fact-find asset-map key, e.g. 'shares' — or 'home' for the family home. */
    assetType: string;
    description: string;
    measure: 'growth' | 'income';
    /** The client's figure, % p.a. */
    chosenPct: number;
    /** What the model would have used had the field been left alone, % p.a. */
    defaultPct: number;
}

/** A number the client actually typed — blank, null or nonsense is "left to the default". */
function typedRate(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

const differs = (a: number, b: number): boolean => Math.abs(a - b) >= 0.005;

/** The overrides on one asset, in the order a reader expects (income before growth). */
function overridesOn(assetType: string, asset: FactFindAsset): AssetRateOverride[] {
    const ac = ASSET_CLASS_ASSUMPTIONS;
    const description = asset.description || 'Unnamed asset';
    const out: AssetRateOverride[] = [];
    const income = typedRate(asset.incomeRate);
    const growth = typedRate(asset.growthRate);
    const push = (measure: AssetRateOverride['measure'], chosen: number | null, fallback: number) => {
        if (chosen !== null && differs(chosen, fallback)) out.push({ assetType, description, measure, chosenPct: chosen, defaultPct: fallback });
    };
    switch (assetType) {
        case 'bankAccounts':
            push('income', income, ac.bankIncomePct);
            break;
        case 'termDeposits':
            push('income', income, ac.termDepositIncomePct);
            break;
        case 'shares':
            push('income', income, ac.sharesIncomePct);
            push('growth', growth, ac.sharesGrowthPct);
            break;
        case 'investmentProperty':
            push('growth', growth, ac.propertyGrowthPct);
            break;
        case 'superannuation':
            if (growth !== null && profileForSuperRate('accumulation', growth) === 'custom') {
                push('growth', growth, INVESTMENT_PROFILES.balanced.accumulationNetPct);
            }
            break;
        case 'accountBasedPensions':
            if (growth !== null && profileForSuperRate('pension', growth) === 'custom') {
                push('growth', growth, INVESTMENT_PROFILES.balanced.pensionNetPct);
            }
            break;
        default:
            break;
    }
    return out;
}

/** The family home's growth, where the client typed their own figure. */
function homeOverride(factFind: RateBearing | null | undefined): AssetRateOverride[] {
    const growth = typedRate(factFind?.home?.homeGrowthRate);
    if (growth === null || !differs(growth, DEFAULT_HOME_GROWTH_PCT)) return [];
    return [{ assetType: 'home', description: 'Family home', measure: 'growth', chosenPct: growth, defaultPct: DEFAULT_HOME_GROWTH_PCT }];
}

/**
 * Every rate on this fact-find that is the client's own figure. Empty when they changed nothing.
 * The home comes first: it is usually the largest number on the balance sheet, and its growth rate
 * moves the estate more than any other single assumption.
 */
export function listAssetRateOverrides(factFind: RateBearing | null | undefined): AssetRateOverride[] {
    const assets = factFind?.assets || {};
    return [
        ...homeOverride(factFind),
        ...Object.entries(assets).flatMap(([assetType, rows]) => (rows || []).flatMap((asset) => overridesOn(assetType, asset))),
    ];
}

export function hasAssetRateOverrides(factFind: RateBearing | null | undefined): boolean {
    return listAssetRateOverrides(factFind).length > 0;
}

/** True where at least one of the client's figures is more optimistic than ours. */
export function anyRateAboveDefault(overrides: AssetRateOverride[]): boolean {
    return overrides.some((o) => o.chosenPct > o.defaultPct);
}

/**
 * The same fact-find with every client-chosen rate put back to the default it displaced. Nothing
 * else is touched — the object returned differs from the input ONLY in those rate fields, so a plan
 * projected on it is the same plan at our rates.
 */
export function withDefaultAssetRates<T extends RateBearing>(factFind: T): T {
    const assets = factFind.assets || {};
    const reset: FactFindAssetMap = {};
    for (const [assetType, rows] of Object.entries(assets)) {
        reset[assetType] = rows?.map((asset) => {
            const overrides = overridesOn(assetType, asset);
            if (!overrides.length) return asset;
            const next: FactFindAsset = { ...asset };
            for (const o of overrides) {
                if (o.measure === 'growth') next.growthRate = o.defaultPct;
                else next.incomeRate = o.defaultPct;
            }
            return next;
        });
    }
    return homeOverride(factFind).length && factFind.home
        ? { ...factFind, assets: reset, home: { ...factFind.home, homeGrowthRate: DEFAULT_HOME_GROWTH_PCT } }
        : { ...factFind, assets: reset };
}
